import styled from 'styled-components'
import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import Button from '../../../shared/Button'
import { useUpdatesContext } from '../state/useUpdates'
import { useImportContext } from '../state/useFileImport'
import { useImportModalContext } from '../state/useImportModal'
import { getProgressByChunk } from '../utils'
import { useFilesImport } from '../state/useFilesImport'
import { useNotificationContext } from '../../notifications/state/useNotification'
import ErrorNotification from '../../notifications/components/ErrorNotification'
import { ROUTES } from '../../../../constants/routes'
import CsvImportModal from './CsvImportModal'

const InputStyled = styled.input`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 100%;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 100%;
`

const ContainerStyled = styled.div`
  display: flex;
  justify-content: space-between;
`

const TitleStyled = styled.h1`
  color: ${({ theme }) => theme.colors.black};
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
`

const REQUIRED_FILES = [
  'dataset.csv',
  'dataset_meta_key.csv',
  'dataset_version.csv',
  'dataset_file.csv',
  'dataset_valueset.csv',
  'dataset_variable.csv',
  'dataset_value.csv',
  'dataset_meta_value.csv'
]

const Header = () => {
  const { importFile, isUploading } = useImportContext()
  const { importFiles, isFilesUploading, filesUploadProgress } = useFilesImport()
  const { fetchUpdates, isFetching } = useUpdatesContext()
  const { setFileName, setProgress } = useImportModalContext()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputCsvRef = useRef<HTMLInputElement>(null)
  const { showErrorNotification } = useNotificationContext()

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const resetCSVInput = () => {
    if (inputCsvRef.current) {
      inputCsvRef.current.value = ''
    }
  }

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.item(0)
    if (!file) return
    resetInput()
    setFileName(file.name)
    setProgress(0)
    await importFile(
      file,
      fetchUpdates,
      (chunk, totalCount) => setProgress(getProgressByChunk(chunk, totalCount))
    )
  }
  const handleCSVImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return

    const files = Array.from(event.target.files)

    if (files.length !== REQUIRED_FILES.length) {
      showErrorNotification(<ErrorNotification message={`Error: you must select exactly ${REQUIRED_FILES.length} files`} />)
      resetCSVInput()
      return
    }

    const fileNames = files.map((f) => f.name)
    const missing = REQUIRED_FILES.filter((req) => !fileNames.includes(req))

    if (missing.length > 0) {
      showErrorNotification(<ErrorNotification message={`Error: missing files: ${missing.join(', ')}`} />)
      resetCSVInput()
      return
    }

    resetCSVInput()
    await importFiles(files, () => {
      fetchUpdates()
      navigate(ROUTES.DATASETS)
    })
  }

  return (
    <>
      <ContainerStyled>
        <InputStyled
          accept=".accdb, .mdb"
          onChange={handleImport}
          type="file"
          ref={inputRef}
        />
        <InputStyled
          accept=".csv"
          onChange={handleCSVImport}
          type="file"
          multiple
          ref={inputCsvRef}
        />
        <TitleStyled>Data Management</TitleStyled>
        <Box display="flex" gap={2}>
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
            <Button loading={isFilesUploading} disabled={isFilesUploading} onClick={() => inputCsvRef.current?.click()}>
              Import csv files
            </Button>
          </Box>
          <Button disabled={isFetching || isUploading} onClick={() => inputRef.current?.click()}>
            Import
          </Button>
        </Box>
      </ContainerStyled>
      <CsvImportModal open={isFilesUploading} progress={filesUploadProgress} />
    </>
  )
}

export default Header
